import { useCallback, useMemo } from 'react'

import { useSafetyTranslation } from 'cng-common/features/translations'
import {
  applyAttentionStatusOffering,
  formatAttentionStatusTitle,
  getAttentionSeverityNameKey,
  getAttentionStatusIntroKey,
  getAttentionStatusRowActionKey,
  type IRaisedAttentionStatus,
  renameAttentionPlanCount,
  splitAttentionStatusTitle,
  type TAttentionStatusRole,
  type TAttentionStatusSeverity,
} from 'partner360/entities/attentionStatuses'

import { FINANCIAL_COCKPIT_TRANSLATION_KEYS } from '../../../configs/translations'
import type { TAttentionGeoLevelId } from '../../../lib'
import { getOfferingTitleKey } from '../../../lib/getOfferingTitleKey'
import type { IAttentionStatusesUnit, IAttentionStatusPhrase } from '../types'

const KEYS = FINANCIAL_COCKPIT_TRANSLATION_KEYS.attentionStatuses

export const STATUS_UNIT: IAttentionStatusesUnit = {
  singularKey: KEYS.units.status.one,
  pluralKey: KEYS.units.status.many,
  stateKey: KEYS.states.raised,
}

/** What the manager cockpit's status level counts under the robot: partners, each once. */
export const PARTNER_ATTENTION_UNIT: IAttentionStatusesUnit = {
  singularKey: KEYS.units.partner.one,
  pluralKey: KEYS.units.partner.many,
  stateKey: KEYS.states.needAttention,
}

export const PARTNER_UNIT: IAttentionStatusesUnit = {
  singularKey: KEYS.units.partner.one,
  pluralKey: KEYS.units.partner.many,
  stateKey: KEYS.states.affected,
}

export const PBM_UNIT: IAttentionStatusesUnit = {
  singularKey: KEYS.units.pbm.one,
  pluralKey: KEYS.units.pbm.many,
  stateKey: KEYS.states.affected,
}

export const MANAGER_UNIT: IAttentionStatusesUnit = {
  singularKey: KEYS.units.manager.one,
  pluralKey: KEYS.units.manager.many,
  stateKey: KEYS.states.affected,
}

/** The unit a geo level counts in, named by the level the drill-down stands on. */
export const getAttentionGeoUnit = (levelId: TAttentionGeoLevelId): IAttentionStatusesUnit => ({
  singularKey: KEYS.units[levelId].one,
  pluralKey: KEYS.units[levelId].many,
  stateKey: KEYS.states.affected,
})

type TParams = Record<string, string | number>

type TTitledStatus = Pick<IRaisedAttentionStatus, 'title' | 'titleTemplate' | 'offerings'>

const toPhrase = (key: string, params: TParams = {}): IAttentionStatusPhrase => ({
  text: key,
  replaceParams: Object.entries(params).map(([name, value]) => ({
    key: `{${name}}`,
    value: String(value),
  })),
})

/**
 * Every sentence the attention-statuses tile says, in one place.
 *
 * The tile's counters are built from parts — a count, the unit it counts in, the state that unit
 * is in — and the three levels of the drill-down differ only in those parts. Composing them here
 * keeps each phrase one key with one set of parameters, rather than a concatenation each layout
 * would have to repeat and each translator would have to guess the word order of.
 *
 * `phrase` holds what the reader sees: a key and its parts, rendered by the caller through
 * `<Translation />`. The one string left is the severity label, and only because it is spoken
 * rather than shown — an `aria-label` takes text, not a component.
 */
export const useAttentionStatusesText = () => {
  const t = useSafetyTranslation()

  /* The decoded title names the offering by its payload name; the catalog's own title is filled
     again with the cockpit's name for it, so a reseller's and a distributor's programme read alike. */
  const offeringTitle = useCallback(
    ({ title, titleTemplate, offerings }: TTitledStatus) =>
      titleTemplate
        ? applyAttentionStatusOffering(titleTemplate, [
            ...new Set(offerings.map((offering) => t(getOfferingTitleKey(offering)))),
          ])
        : title,
    [t]
  )

  const fill = useCallback(
    (key: string, params: TParams = {}) =>
      Object.entries(params).reduce(
        (text, [name, value]) => text.replaceAll(`{${name}}`, String(value)),
        t(key)
      ),
    [t]
  )

  return useMemo(() => {
    /** The unit's own word, singular or plural by what it counts. */
    const unitWord = (unit: IAttentionStatusesUnit, count: number) =>
      t(count === 1 ? unit.singularKey : unit.pluralKey)

    /** `Highest` — the severity's own word, a part rather than a sentence of its own. */
    const severityWord = (severity: TAttentionStatusSeverity) =>
      t(getAttentionSeverityNameKey(severity))

    return {
      /** What the tile renders; each one is spread into `<Translation />` by its caller. */
      phrase: {
        /** `of 11 statuses raised` — the hero's line under its number. */
        counter: (unit: IAttentionStatusesUnit, total: number) =>
          toPhrase(KEYS.counter, { total, unit: unitWord(unit, total), state: t(unit.stateKey) }),

        /** `3 statuses raised` — a level with no total, so the count stands on its own. */
        raised: (unit: IAttentionStatusesUnit, count: number) =>
          toPhrase(KEYS.raised, { count, unit: unitWord(unit, count), state: t(unit.stateKey) }),

        /** `5 statuses not raised` — what the level is not showing. */
        rest: (unit: IAttentionStatusesUnit, count: number) =>
          toPhrase(KEYS.rest, { count, unit: unitWord(unit, count), state: t(unit.stateKey) }),

        /** `0 of 11 statuses raised` — the empty state's line. */
        fullCounter: (unit: IAttentionStatusesUnit, count: number, total: number) =>
          toPhrase(KEYS.fullCounter, {
            count,
            total,
            unit: unitWord(unit, total),
            state: t(unit.stateKey),
          }),

        /** `43 of 80` — a score against its target. */
        score: (value: number, target: number) => toPhrase(KEYS.score, { value, target }),

        /** `Highest priority` — the badge's label, and the detail's own line. */
        severityLabel: (severity: TAttentionStatusSeverity) =>
          toPhrase(KEYS.severityPriority, { severity: severityWord(severity) }),

        /** `Highest` — the board column's own head. */
        severityName: (severity: TAttentionStatusSeverity) =>
          toPhrase(getAttentionSeverityNameKey(severity)),

        /** What an opened status shows, said above its detail, worded for the reader's role. */
        introText: (bitValue: number, role?: TAttentionStatusRole, isDistributor = false) =>
          toPhrase(getAttentionStatusIntroKey(bitValue, role, isDistributor)),

        /** `The markets this status is raised in.` — what a drilled level lists, said above it. */
        levelIntro: (unit: IAttentionStatusesUnit, total: number) =>
          toPhrase(KEYS.levelIntro, { unit: unitWord(unit, total) }),

        /** `Acknowledge`, `Update SOB` — what the status' action is called, on the row, for a role
            that may take it; `See details` for a role that only reads the status. */
        actionLabel: (bitValue: number, role: TAttentionStatusRole) =>
          toPhrase(getAttentionStatusRowActionKey(bitValue, role)),

        /** `Open market` — the action of a row that is a level rather than a subject. */
        openLevel: (unit: IAttentionStatusesUnit) =>
          toPhrase(KEYS.openLevel, { unit: t(unit.singularKey) }),

        /** `Group Germany by` — the grouping menu's heading. */
        groupingApplyTo: (level: string) => toPhrase(KEYS.grouping.applyTo, { level }),

        /** `41 of 58 partners with open statuses` — what the full-screen level holds. */
        partnersWithOpen: (count: number, total: number) =>
          toPhrase(KEYS.fullscreen.partnersWithOpen, { count, total }),

        /** `Open in Germany, by status` — the full-screen summary's first panel. */
        openByStatus: (scope: string) => toPhrase(KEYS.fullscreen.openByStatus, { scope }),

        /** `PBMs with most open in Germany` — the second panel. */
        topPbms: (scope: string) => toPhrase(KEYS.fullscreen.topPbms, { scope }),

        /** `34 open` — how many statuses an owner's partners hold open. */
        openCount: (count: number) => toPhrase(KEYS.fullscreen.openCount, { count }),
      },

      /** `Remind Nadia Wolf` — a reminder button's tooltip and accessible name. */
      remindLabel: (name: string) => fill(KEYS.reminder.remind, { name }),

      /** `Nadia Wolf reminded` — the same button once the reminder went out. */
      remindedLabel: (name: string) => fill(KEYS.reminder.sent, { name }),

      /** `Replace Nadia Wolf` — the swap button's tooltip and accessible name. */
      replaceLabel: (name: string) => fill(KEYS.replacement.replace, { name }),

      /** `Highest priority` read out for a badge that shows only its number: an `aria-label`. */
      severityLabel: (severity: TAttentionStatusSeverity) =>
        fill(KEYS.severityPriority, { severity: severityWord(severity) }),

      /**
       * A status' own title, with the catalog's placeholders filled.
       *
       * The offering is filled at decoding, where the mask that raised the status names it. What
       * is left here is the row no single offering stands behind — `Overall` reading several, or
       * a group of partners raising the same status for different ones. That row reads the plain
       * word rather than the catalog's `Offering X`, which is an instruction to the reader that
       * nobody meant to give.
       */
      statusTitle: (status: TTitledStatus, raisedCount?: number, scopeSize?: number) =>
        formatAttentionStatusTitle({
          title: offeringTitle(status),
          raisedCount,
          scopeSize,
          offering: t(KEYS.offeringGeneric),
        }),

      /** The same status titled by partners where it counts plans — `X plans …` reads
       *  `X partners …` — for the distributors' view, where one partner is one plan. */
      countedByPartner: <TStatus extends TTitledStatus>(status: TStatus): TStatus => {
        const partnerNoun = { one: t(KEYS.units.partner.one), many: t(KEYS.units.partner.many) }

        return {
          ...status,
          title: renameAttentionPlanCount(status.title, partnerNoun),
          titleTemplate:
            status.titleTemplate && renameAttentionPlanCount(status.titleTemplate, partnerNoun),
        }
      },

      /** The same title with its count handed over on its own, for a row that sets it in its own
       *  weight rather than in the sentence's. */
      statusTitleParts: (status: TTitledStatus, raisedCount?: number) =>
        splitAttentionStatusTitle({
          title: offeringTitle(status),
          raisedCount,
          offering: t(KEYS.offeringGeneric),
        }),
    }
  }, [fill, offeringTitle, t])
}

export type TAttentionStatusesText = ReturnType<typeof useAttentionStatusesText>
